// Catering (backend migration 131). A restaurant marks categories as catering;
// the items in them are ordered as a cart of their own, placed ahead of time.
// Pure helpers shared by the menu page, the cart guard, checkout and the
// Catering search page. The rules mirror rushmyfood-backend/src/utils/catering.js.

// Catering's accent: the site's own red, with the soft tint and border the
// header's partner link uses. (It was purple until 2026-10-16.)
export const CATERING_COLOR = "#CC0000";
export const CATERING_SOFT = "#FFF1EE";
export const CATERING_BORDER = "#FFE2DA";
export const CATERING_DARK = "#B30000";

// What the backend assumes when a restaurant has saved nothing.
const DEFAULT_SETTINGS = {
  leadTimeMinutes: 1440,
  prepTimeMinutes: 60,
  deliveryMode: "REGULAR",
  allowPickup: true,
  minimumOrder: null,
  serviceFee: null,
};

const norm = (s) => String(s || "").trim().toLowerCase();

/** Lower-cased titles of the restaurant's catering categories. */
export function cateringCategoryKeys(categories) {
  return new Set(
    (categories || []).filter((c) => c && c.isCatering).map((c) => norm(c.title)).filter(Boolean)
  );
}

/**
 * Ids of every catering item on this restaurant's menu. An advanced-menu item
 * is catering when its category name matches a catering category (case and
 * spaces ignored); a legacy food when its category is catering. Cart lines
 * carry the item id as `_id`, so this is how a cart's kind is told.
 */
export function cateringItemIds(restaurant) {
  const ids = new Set();
  if (!restaurant) return ids;
  const keys = cateringCategoryKeys(restaurant.categories);
  if (!keys.size) return ids;
  const menus = restaurant.advancedMenus || [];
  const usesAdvanced = menus.some((m) => m.isActive && m.items && m.items.length > 0);
  if (usesAdvanced) {
    menus.forEach((m) =>
      (m.items || []).forEach((it) => {
        if (keys.has(norm(it.categoryName))) ids.add(String(it._id));
      })
    );
  } else {
    (restaurant.categories || []).forEach((c) => {
      if (c.isCatering) (c.foods || []).forEach((f) => ids.add(String(f._id)));
    });
  }
  return ids;
}

/** True when any line in the cart is a catering item. */
export function isCateringCart(cart, cateringIds) {
  if (!cateringIds || !cateringIds.size) return false;
  return (cart || []).some((line) => cateringIds.has(String(line._id)));
}

/** The restaurant's catering settings, with the backend defaults filled in. */
export function cateringSettingsOf(restaurant) {
  const c = (restaurant && restaurant.catering) || {};
  const int = (v, fallback) => (v != null && Number.isFinite(Number(v)) ? Number(v) : fallback);
  return {
    leadTimeMinutes: int(c.leadTimeMinutes, DEFAULT_SETTINGS.leadTimeMinutes),
    prepTimeMinutes: int(c.prepTimeMinutes, DEFAULT_SETTINGS.prepTimeMinutes),
    deliveryMode: c.deliveryMode || DEFAULT_SETTINGS.deliveryMode,
    allowPickup: c.allowPickup != null ? !!c.allowPickup : DEFAULT_SETTINGS.allowPickup,
    minimumOrder: c.minimumOrder != null && Number(c.minimumOrder) > 0 ? Number(c.minimumOrder) : null,
    serviceFee: c.serviceFee != null ? Number(c.serviceFee) : null,
  };
}

/** "24 hours", "2 days", "90 minutes" — same wording as the backend. */
export function describeLead(minutes) {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  if (m >= 2880 && m % 1440 === 0) return `${m / 1440} days`;
  if (m >= 120 && m % 60 === 0) return `${m / 60} hours`;
  if (m === 60) return "1 hour";
  return `${m} minutes`;
}

/** "Order 24 hours ahead", or null when no notice is needed. */
export function cateringNotice(leadTimeMinutes) {
  const m = Math.round(Number(leadTimeMinutes) || 0);
  return m > 0 ? `Order ${describeLead(m)} ahead` : null;
}

/** "Delivery or pickup" / "Delivery only" / "Pickup only". */
export function cateringDeliveryLine(settings) {
  if (!settings) return null;
  if (settings.deliveryMode === "PICKUP_ONLY") return "Pickup only";
  return settings.allowPickup === false ? "Delivery only" : "Delivery or pickup";
}

/** "Catering minimum $150", or null when there is none. */
export function cateringMinimumLabel(amount, currencySymbol = "$") {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return null;
  const shown = Number.isInteger(n) ? String(n) : n.toFixed(2);
  return `Catering minimum ${currencySymbol}${shown}`;
}

/** Can a catering order from this restaurant be delivered at all? */
export function cateringDeliveryOffered(settings, regularDeliveryOffered) {
  if (!settings) return regularDeliveryOffered;
  if (settings.deliveryMode === "PICKUP_ONLY") return false;
  if (settings.deliveryMode === "REGULAR") return regularDeliveryOffered;
  return true;
}

/**
 * The earliest time a catering order can be wanted for: now + notice, rounded
 * up to the next quarter hour so the first slot reads as a clean time.
 */
export function cateringEarliest(leadTimeMinutes, now = Date.now()) {
  const lead = Math.max(0, Number(leadTimeMinutes) || 0);
  const quarter = 15 * 60 * 1000;
  return new Date(Math.ceil((now + lead * 60000) / quarter) * quarter);
}

/** Does a search query ask for catering? ("cater", "catering", "caterers"…) */
export function queryMentionsCatering(query) {
  const q = norm(query);
  if (!q) return false;
  return q.includes("cater") || (q.length >= 4 && "catering".startsWith(q));
}

/**
 * The day a caterer can take an event planned for `at`: that day when its
 * notice allows, otherwise the first later day at the same time of day.
 */
export function cateringDateFor(at, leadTimeMinutes, now = Date.now()) {
  const wanted = new Date(at);
  const floor = cateringEarliest(leadTimeMinutes, now);
  if (wanted >= floor) return wanted;
  const next = new Date(floor);
  next.setHours(wanted.getHours(), wanted.getMinutes(), 0, 0);
  if (next < floor) next.setDate(next.getDate() + 1);
  return next;
}

// The event a customer scheduled from the Catering page ("Schedule for Oct
// 24"), so that restaurant's catering checkout starts on that time.
const PLAN_KEY = "cateringPlan";

export function saveCateringPlan(restaurantId, at, guests) {
  try {
    localStorage.setItem(
      PLAN_KEY,
      JSON.stringify({ restaurantId: String(restaurantId), at: new Date(at).toISOString(), guests })
    );
  } catch (e) {
    // Storage blocked (private mode): checkout just starts on the earliest time.
  }
}

/** Forget the saved event (the Catering page went back to Anytime). */
export function clearCateringPlan() {
  try {
    localStorage.removeItem(PLAN_KEY);
  } catch (e) {
    // Nothing saved to clear.
  }
}

/** The saved event for this restaurant, while it is still ahead; else null. */
export function readCateringPlan(restaurantId) {
  try {
    const plan = JSON.parse(localStorage.getItem(PLAN_KEY) || "null");
    if (!plan || String(plan.restaurantId) !== String(restaurantId)) return null;
    const at = new Date(plan.at);
    if (!(at.getTime() > Date.now())) return null;
    return { ...plan, at };
  } catch (e) {
    return null;
  }
}

// The guest count a customer is planning for: the event saved for this
// restaurant, else the Catering page's event (kept for the browsing session),
// else 25.
export const CATERING_PLANNER_KEY = "cateringPlanner";
const DEFAULT_GUESTS = 25;

export function readCateringGuests(restaurantId) {
  const fromPlan = readCateringPlan(restaurantId)?.guests;
  if (Number.isInteger(fromPlan) && fromPlan > 0) return fromPlan;
  try {
    const planner = JSON.parse(sessionStorage.getItem(CATERING_PLANNER_KEY) || "null");
    if (Number.isInteger(planner?.guests) && planner.guests > 0) return planner.guests;
  } catch (e) {
    // Fall through to the default.
  }
  return DEFAULT_GUESTS;
}
